
import React, { useRef, useEffect, memo } from 'react';

// Helper to remove markdown artifacts and fix common LaTeX errors
export const cleanLatex = (text: string, isPrintMode: boolean = false): string => {
    if (!text) return '';
    let cleaned = text
        .replace(/^```(?:markdown)?\s*/i, '') // Remove starting code block
        .replace(/```$/i, '') // Remove ending code block
        .replace(/^markdown\s*/i, '') // Remove "markdown" word at very beginning
        .trim();

    // 1. Fix invalid single-letter variable commands (\x -> x, \p -> p, etc.)
    cleaned = cleaned.replace(/\\([xypkXYPKnma])(?![a-zA-Z])/g, '$1');

    // 2. Fix \limits abuse
    cleaned = cleaned.replace(/\\limits(its)?/g, '');

    // 3. Re-apply \limits STRICTLY to supported operators
    cleaned = cleaned.replace(/\\(sum|lim|prod|bigcup|bigcap)(?![a-zA-Z])/g, '\\$1\\limits');

    // 4. Fix \textcircled{...} by converting to Unicode
    cleaned = cleaned.replace(/\\textcircled\{([ㄱ-ㅎ가-힣a-zA-Z0-9]+)\}/g, (match, char) => {
        const map: Record<string, string> = {
            'ㄱ': '㉠', 'ㄴ': '㉡', 'ㄷ': '㉢', 'ㄹ': '㉣', 'ㅁ': '㉤', 'ㅂ': '㉥', 'ㅅ': '㉦', 'ㅇ': '㉧', 'ㅈ': '㉨', 'ㅊ': '㉩', 'ㅋ': '㉪', 'ㅌ': '㉫', 'ㅍ': '㉬', 'ㅎ': '㉭',
            '가': '㉮', '나': '㉯', '다': '㉰', '라': '㉱', '마': '㉲', '바': '㉳', '사': '㉴', '아': '㉵', '자': '㉶', '차': '㉷', '카': '㉸', '타': '㉹', '파': '㉺', '하': '㉻',
            'a': 'ⓐ', 'b': 'ⓑ', 'c': 'ⓒ', 'd': 'ⓓ', 'e': 'ⓔ', 'f': 'ⓕ', 'g': 'ⓖ', 'h': 'ⓗ', 'i': 'ⓘ', 'j': 'ⓙ', 'k': 'ⓚ', 'l': 'ⓛ', 'm': 'ⓜ', 'n': 'ⓝ', 'o': 'ⓞ', 'p': 'ⓟ', 'q': 'ⓠ', 'r': 'ⓡ', 's': 'ⓢ', 't': 'ⓣ', 'u': 'ⓤ', 'v': 'ⓥ', 'w': 'ⓦ', 'x': 'ⓧ', 'y': 'ⓨ', 'z': 'ⓩ',
            'A': 'Ⓐ', 'B': 'Ⓑ', 'C': 'Ⓒ', 'D': 'Ⓓ', 'E': 'Ⓔ', 'F': 'Ⓕ', 'G': 'Ⓖ', 'H': 'Ⓗ', 'I': 'Ⓘ', 'J': 'Ⓙ', 'K': 'Ⓚ', 'L': 'Ⓛ', 'M': 'Ⓜ', 'N': 'Ⓝ', 'O': 'Ⓞ', 'P': 'Ⓟ', 'Q': 'Ⓠ', 'R': 'Ⓡ', 'S': 'Ⓢ', 'T': 'Ⓣ', 'U': 'Ⓤ', 'V': 'Ⓥ', 'W': 'Ⓦ', 'X': 'Ⓧ', 'Y': 'Ⓨ', 'Z': 'Ⓩ',
            '1': '①', '2': '②', '3': '③', '4': '④', '5': '⑤', '6': '⑥', '7': '⑦', '8': '⑧', '9': '⑨', '10': '⑩', '11': '⑪', '12': '⑫', '13': '⑬', '14': '⑭', '15': '⑮'
        };
        return map[char] || match;
    });

    // 5. Fix broken display math and "dollar visible" issue
    cleaned = cleaned.replace(/(\$\$)\s*[\r\n]+/g, '$1');
    cleaned = cleaned.replace(/\$\$\s*\n+([\s\S]*?)\n+\s*\$\$/g, '$$$1$$');

    // 6. Add spacing around inequalities
    cleaned = cleaned.replace(/([≤≥])/g, ' $1 ');
    cleaned = cleaned.replace(/(\\(?:le|ge|leq|geq|leqq|geqq)(?![a-zA-Z]))/g, ' $1 ');
    cleaned = cleaned.replace(/</g, ' < ');
    cleaned = cleaned.replace(/>/g, ' > ');
    
    // 7. Fix OCR "ext" artifacts (e.g., "extA" -> "\text{A}")
    // Look for 'ext' followed by a letter, preceded by space or start of line or non-backslash
    cleaned = cleaned.replace(/(^|[^a-zA-Z\\])ext([A-Za-z])/g, '$1\\text{$2}');
    // Look for 'ext{' pattern which missed the backslash
    cleaned = cleaned.replace(/(^|[^a-zA-Z\\])ext\{/g, '$1\\text{');

    // Restore <보기>
    cleaned = cleaned.replace(/ < 보기 > /g, '<보기>');

    if (isPrintMode) {
        cleaned = cleaned.replace(/(\n\s*){2,}/g, '\n'); 
        cleaned = cleaned.replace(/([^\n])\s*(\$\$)/g, '$1\n$2'); 
        cleaned = cleaned.replace(/(\$\$)\s*([^\n])/g, '$1\n$2');
    }

    return cleaned.trim();
};

const LatexRenderer: React.FC<{ latex: string; preserveLinebreaks?: boolean; disableAutoAlign?: boolean; isPrintMode?: boolean; }> = ({ latex, preserveLinebreaks = false, disableAutoAlign = false, isPrintMode = false }) => {
  const containerRef = useRef<HTMLSpanElement>(null);
  
  const processedContent = React.useMemo(() => {
      let rawLatex = latex || '';
      rawLatex = cleanLatex(rawLatex, isPrintMode);
      rawLatex = rawLatex.replace(/[`´]/g, '');

      const mathRegex = /(\$\$[\s\S]+?\$\$|\\\[[\s\S]+?\\\]|(?<!\\)\$(?:\\.|[^\$])+(?<!\\)\$|\\\([\s\S]+?\\\))/g;
      
      const parts = rawLatex.split(mathRegex);
      
      const initialBlocks = parts.map((part, index) => {
          if (index % 2 === 0) {
              if (!part) return { type: 'text', content: '' };
              let text = part;
              text = text.replace(/&(?![a-zA-Z\d]+;)/g, '\\&');
              return { type: 'text', content: text };
          } else {
              return { type: 'math', content: part, isDisplay: part.startsWith('$$') || part.startsWith('\\[') };
          }
      });

      const processedBlocks = initialBlocks.map(block => {
          if (block.type === 'math' && block.isDisplay && !disableAutoAlign) {
              let content = block.content;
              let inner = content;
              
              if (content.startsWith('$$')) inner = content.slice(2, -2);
              else if (content.startsWith('\\[')) inner = content.slice(2, -2);

              const trimmedInner = inner.trim();
              const hasEnvironment = /\\begin\{/.test(trimmedInner);
              const hasAlignChar = /&/.test(trimmedInner);
              const hasNewline = /\\\\/.test(trimmedInner);

              if (!hasEnvironment && (hasAlignChar || hasNewline)) {
                  return { 
                      ...block, 
                      content: `$$\\begin{aligned}\n${trimmedInner}\n\\end{aligned}$$` 
                  };
              }
          }
          return block;
      });

      return processedBlocks;
  }, [latex, preserveLinebreaks, disableAutoAlign, isPrintMode]);

  useEffect(() => {
    if (containerRef.current && (window as any).MathJax) {
      (window as any).MathJax.typesetClear([containerRef.current]);
      requestAnimationFrame(() => {
          if(containerRef.current) {
             (window as any).MathJax.typesetPromise([containerRef.current]).catch((err: any) => {
                 if ((window as any).MathJax.typeset) (window as any).MathJax.typeset([containerRef.current]);
             });
          }
      });
    }
  }, [processedContent]);

  const choiceLayout = React.useMemo(() => {
      const fullText = processedContent.map(p => p.content).join('');
      
      if (fullText.includes('①') && fullText.includes('②')) {
          const rawLatexForChoices = fullText.replace(/\r?\n/g, ' ').trim();
          const splitRegex = /(?=[①②③④⑤])/;
          const possibleChoices = rawLatexForChoices.split(splitRegex).filter(c => c.trim() !== '');
          const choiceMarkers = ['①', '②', '③', '④', '⑤'];
          
          const hasMultipleChoices = 
            possibleChoices.length >= 2 && 
            possibleChoices.length <= 5 && 
            possibleChoices.every(c => choiceMarkers.some(m => c.trim().startsWith(m)));
            
          if (hasMultipleChoices) {
              return possibleChoices.map(choice => {
                  const match = choice.match(/^([①-⑤])\s*(.+)$/);
                  if (match) {
                      const marker = match[1];
                      const body = match[2].trim();
                      
                      const hasMathDelim = body.includes('$') || body.includes('\\(');
                      const hasKorean = /[ㄱ-ㅎ|ㅏ-ㅣ|가-힣]/.test(body);
                      
                      if (!hasMathDelim && !hasKorean) {
                          return `${marker} $${body}$`;
                      }
                  }
                  return choice;
              });
          }
      }
      return null;
  }, [processedContent]);

  if (choiceLayout) {
      return (
        <div ref={containerRef as any} className="grid grid-cols-5 gap-2 w-full mt-1">
          {choiceLayout.map((choice, index) => {
            return (
                <div key={index} className="min-w-0">
                    <LatexRenderer latex={choice} isPrintMode={isPrintMode} />
                </div>
            );
          })}
        </div>
      );
  }
    
  return (
    <span ref={containerRef}>
      {processedContent.map((part, index) => {
        if (part.type === 'text') {
             if (!part.content) return null;
             if (preserveLinebreaks) {
                 return <span key={index} style={{ whiteSpace: 'pre-wrap' }}>{part.content}</span>;
             }
             return <React.Fragment key={index}>{part.content}</React.Fragment>;
        } else {
             return <span key={index} dangerouslySetInnerHTML={{ __html: part.content }} />;
        }
      })}
    </span>
  );
};

export default memo(LatexRenderer);
